/**
 * useUnifiedSessions Hook
 *
 * Fetches all sessions from the unified Reports AI endpoint.
 * Tags each session with source based on metadata.purpose.
 */

import { useState, useEffect, useCallback } from 'react';
import ApiReportsChatbot from '../api/reports-chatbot';
import ApiSubjectManagementAi from '../api/subjectManagementAi';

/**
 * Enhance sessions with source tags from metadata.purpose
 * @param {Array} sessions - Raw sessions from API
 * @returns {Array} Enhanced sessions
 */
const enhanceSessions = (sessions) => {
  if (!Array.isArray(sessions)) return [];

  return sessions.map(session => {
    // Determine source from session metadata
    const purpose = session.metadata && session.metadata.purpose;
    const source = purpose === 'subject-management-assistant'
      ? 'subject-management'
      : 'reports';

    return {
      ...session,
      source,
      // Strip legacy prefix if present
      title: session.title ? session.title.replace(/^(Analytics|Subject):\s*/, '') : 'Untitled Session'
    };
  });
};

/**
 * Custom hook for unified session management
 * @param {string} userId - Current user ID
 * @returns {Object} Session management state and functions
 */
const useUnifiedSessions = (userId) => {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [warning, setWarning] = useState(null);

  /**
   * Load all sessions from the unified API (no purpose filter = all sessions)
   */
  const loadSessions = useCallback(async () => {
    if (!userId) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setWarning(null);

      // Single API call - no purpose filter returns all sessions
      const result = await ApiReportsChatbot.listSessions(userId);

      const allSessions = result.data &&
                          result.data.data &&
                          result.data.data.sessions
        ? enhanceSessions(result.data.data.sessions)
        : [];

      // Sort by lastActivity (most recent first)
      allSessions.sort((a, b) => {
        const aDate = new Date(a.lastActivity || a.updatedAt || a.createdAt);
        const bDate = new Date(b.lastActivity || b.updatedAt || b.createdAt);
        return bDate - aDate;
      });

      setSessions(allSessions);
    } catch (err) {
      console.error('Error loading sessions:', err);
      setError('Failed to load sessions');
      setSessions([]);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  /**
   * Create a new session via the appropriate API
   * @param {Object} apiClient - ApiSubjectManagementAi or ApiReportsChatbot
   * @param {string} title - Session title
   * @param {Object} metadata - Session metadata
   * @returns {Promise<Object>} Created session
   */
  const createSession = useCallback(async (apiClient, title, metadata = {}) => {
    if (!userId) throw new Error('User ID is required');

    const source = apiClient === ApiSubjectManagementAi
      ? 'subject-management'
      : 'reports';

    const prefix = source === 'subject-management' ? 'Subject:' : 'Analytics:';

    // Add prefix if not already present
    const fullTitle = title && (
      title.startsWith('Subject:') ||
      title.startsWith('Analytics:')
    ) ? title : `${prefix} ${title || 'New Session'}`;

    // Determine purpose for metadata
    const purpose = source === 'subject-management'
      ? 'subject-management-assistant'
      : 'reports-assistant';

    const response = await apiClient.createSession(userId, fullTitle, null, {
      purpose,
      ...metadata
    });

    // Extract session from response
    const newSession = response.data && response.data.data
      ? response.data.data
      : response.data;

    // Return session with source tag
    return {
      ...newSession,
      source,
      title: fullTitle
    };
  }, [userId]);

  /**
   * Delete a session via the appropriate API
   * @param {string} sessionId - Session ID to delete
   * @param {string} source - 'subject-management' or 'reports'
   * @param {boolean} permanent - Whether to permanently delete
   * @returns {Promise}
   */
  const deleteSession = useCallback(async (sessionId, source, permanent = true) => {
    if (!userId) throw new Error('User ID is required');
    if (!sessionId) throw new Error('Session ID is required');

    // Both sources now use the same reports-ai endpoint
    const apiClient = source === 'subject-management'
      ? ApiSubjectManagementAi
      : ApiReportsChatbot;

    await apiClient.deleteSession(sessionId, userId, permanent);

    // Remove from local state immediately for optimistic update
    setSessions(prev => prev.filter(s => s.id !== sessionId));
  }, [userId]);

  /**
   * Refresh sessions
   */
  const refreshSessions = useCallback(() => {
    return loadSessions();
  }, [loadSessions]);

  // Load sessions on mount and when userId changes
  useEffect(() => {
    loadSessions();
  }, [loadSessions]);

  return {
    sessions,
    loading,
    error,
    warning,
    createSession,
    deleteSession,
    refreshSessions
  };
};

export default useUnifiedSessions;
